import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter 
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Folder,
  Laptop,
  Users,
  Building,
  Megaphone,
  Shield,
  Plane,
  Briefcase,
  ShoppingCart,
  Phone,
  Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  folder: Folder,
  laptop: Laptop,
  users: Users,
  building: Building,
  megaphone: Megaphone,
  shield: Shield,
  plane: Plane,
  briefcase: Briefcase,
  "shopping-cart": ShoppingCart,
  phone: Phone,
};

const COLORS = [
  "#E31E24",
  "#09090b",
  "#71717a",
  "#ef4444",
  "#f59e0b",
  "#22c55e",
  "#3b82f6",
  "#8b5cf6",
];

const initialCategory = {
  name: "",
  color: "#E31E24",
  icon: "folder",
};

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState(initialCategory);
  const [saving, setSaving] = useState(false);
  const { t } = useLanguage();

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      toast.error(t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleOpenDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        color: category.color,
        icon: category.icon,
      });
    } else {
      setEditingCategory(null);
      setFormData(initialCategory);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCategory(null);
    setFormData(initialCategory);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, formData);
        toast.success(t("common.saved"));
      } else {
        await axios.post(`${API}/categories`, formData);
        toast.success(t("common.saved"));
      }

      handleCloseDialog();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("common.confirmDelete"))) return;

    try {
      await axios.delete(`${API}/categories/${id}`);
      toast.success(t("common.deleted"));
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("common.error"));
    }
  };

  const predefinedCategories = categories.filter(c => c.is_predefined);
  const customCategories = categories.filter(c => !c.is_predefined);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-10 bg-muted rounded animate-pulse w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-16 bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="categories-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-[Manrope] tracking-tight">{t("categories.title")}</h1>
          <p className="text-muted-foreground mt-1">
            {t("categories.subtitle")}
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#E31E24] hover:bg-[#c91a1f]"
              onClick={() => handleOpenDialog()}
              data-testid="add-category-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t("categories.newCategory")}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle className="font-[Manrope]">
                {editingCategory ? t("categories.editCategory") : t("categories.newCategory")}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="name">{t("categories.name")} *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="z.B. Weiterbildung"
                  required
                  data-testid="category-name"
                />
              </div>
              <div>
                <Label>{t("categories.color")}</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={`h-8 w-8 rounded-full border-2 transition-transform ${
                        formData.color === color 
                          ? "border-foreground scale-110" 
                          : "border-transparent hover:scale-105"
                      }`}
                      style={{ backgroundColor: color }}
                      data-testid={`color-${color}`}
                    />
                  ))}
                </div>
              </div>
              <div>
                <Label>{t("categories.icon")}</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {Object.entries(ICON_MAP).map(([key, Icon]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setFormData({ ...formData, icon: key })}
                      className={`h-10 w-10 rounded-md border flex items-center justify-center transition-colors ${
                        formData.icon === key 
                          ? "border-foreground bg-muted" 
                          : "border-border hover:bg-muted"
                      }`}
                      data-testid={`icon-${key}`}
                    >
                      <Icon className="h-5 w-5" />
                    </button>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  {t("common.cancel")}
                </Button>
                <Button 
                  type="submit" 
                  className="bg-[#E31E24] hover:bg-[#c91a1f]"
                  disabled={saving}
                  data-testid="save-category-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t("common.loading")}
                    </>
                  ) : (
                    t("common.save")
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Predefined Categories */}
      <div>
        <h2 className="text-lg font-semibold font-[Manrope] mb-4">{t("categories.predefined")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {predefinedCategories.map((category) => {
            const Icon = ICON_MAP[category.icon] || Folder;
            return (
              <Card 
                key={category.id} 
                className="card-hover"
                data-testid={`category-${category.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div 
                      className="h-12 w-12 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${category.color}15` }}
                    >
                      <Icon className="h-6 w-6" style={{ color: category.color }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium">{category.name}</h3>
                      <Badge variant="secondary" className="mt-1 text-xs">
                        {t("categories.predefined")}
                      </Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenDialog(category)}
                      data-testid={`edit-category-${category.id}`}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Custom Categories */}
      <div>
        <h2 className="text-lg font-semibold font-[Manrope] mb-4">{t("categories.custom")}</h2>
        {customCategories.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              <Folder className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t("categories.noCustom")}</p>
              <p className="text-sm">{t("categories.createHint")}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {customCategories.map((category) => {
              const Icon = ICON_MAP[category.icon] || Folder;
              return (
                <Card 
                  key={category.id} 
                  className="card-hover"
                  data-testid={`category-${category.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div 
                        className="h-12 w-12 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${category.color}15` }}
                      >
                        <Icon className="h-6 w-6" style={{ color: category.color }} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium">{category.name}</h3>
                        <Badge variant="outline" className="mt-1 text-xs">
                          Eigene
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(category)}
                          data-testid={`edit-category-${category.id}`}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(category.id)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-category-${category.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
